/**
 * <mf-choice> — "Choose your frame": ready design from the collection or "customize it yourself".
 * The shopper's choice is applied by clicking the matching inputs of the theme's own variant picker
 * (kept in the page but hidden by CSS), one option at a time, so Horizon keeps handling the variant,
 * price, media, availability and the product form.
 */
import { StandardEvents } from '@shopify/events';

const UPDATE_TIMEOUT = 20000;

class MfChoice extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {Promise<void>} */
  #queue = Promise.resolve();

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('change', (event) => this.#onChange(event), { signal });

    // If the theme picker's inputs are missing, show the theme picker again so the page never loses its variant controls.
    const ready =
      this.#nativeInputs(this.dataset.designOption).length > 0 && this.#nativeInputs(this.dataset.songsOption).length > 0;
    this.toggleAttribute('data-fallback', !ready);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  get #scope() {
    return this.closest('.shopify-section, dialog') || document;
  }

  /** @param {string | undefined} optionName */
  #nativeInputs(optionName) {
    if (!optionName) return [];
    return [...this.#scope.querySelectorAll('variant-picker input[type="radio"]')].filter(
      (input) => input instanceof HTMLInputElement && input.dataset.optionName === optionName
    );
  }

  /** @param {Event} event */
  #onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.role) return;

    if (input.dataset.role === 'path') {
      const custom = input.value === 'custom';
      const panel = this.querySelector('[data-ready-panel]');
      if (panel instanceof HTMLElement) panel.hidden = custom;
      const status = this.querySelector('[data-status]');
      const title = input.closest('label')?.querySelector('.mf-choice__path-title')?.textContent?.trim();
      if (status && title) status.textContent = title;
    }

    // Selections are applied in order; each run reads the latest state, so fast clicks settle correctly.
    this.#queue = this.#queue.then(() => this.#sync()).catch(() => {});
  }

  #state() {
    const path = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-role="path"]:checked'))?.value;
    if (path === 'custom') {
      return { design: this.dataset.customValue, songs: this.dataset.mineValue };
    }
    return {
      design: /** @type {HTMLInputElement | null} */ (this.querySelector('[data-role="design"]:checked'))?.value,
      songs: /** @type {HTMLInputElement | null} */ (this.querySelector('[data-role="songs"]:checked'))?.value,
    };
  }

  async #sync() {
    this.setAttribute('aria-busy', 'true');
    try {
      const { design, songs } = this.#state();
      await this.#select(this.dataset.designOption, design);
      await this.#select(this.dataset.songsOption, songs);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Clicks the theme picker input for one option value and waits until the picker has re-rendered.
   * @param {string | undefined} optionName
   * @param {string | undefined} value
   */
  async #select(optionName, value) {
    if (!optionName || value === undefined) return;
    const input = this.#nativeInputs(optionName).find((el) => el.value === value);
    if (!input || input.checked) return;

    const updated = this.#nextUpdate();
    input.click();
    await updated;
  }

  /** Resolves when the next product selection has finished (or failed), with a safety timeout. */
  #nextUpdate() {
    return new Promise((resolve) => {
      const scope = this.#scope;
      const timer = setTimeout(done, UPDATE_TIMEOUT);
      function done() {
        clearTimeout(timer);
        scope.removeEventListener(StandardEvents.productSelect, onSelect);
        resolve(undefined);
      }
      /** @param {Event & { promise?: Promise<unknown> }} event */
      function onSelect(event) {
        Promise.resolve(event.promise).then(done, done);
      }
      scope.addEventListener(StandardEvents.productSelect, onSelect, { once: true });
    });
  }
}

if (!customElements.get('mf-choice')) customElements.define('mf-choice', MfChoice);
